/*
 * fleetGatewayCard() — base-ui's `.base-overview-card` chrome (same size/border/hover/
 * title font, NO left stripe) + the design's body: gateway-id subline, a CPU/MEM/TEMP
 * ring row, reused `statusBadge`, and a 2-col hardware grid.
 *
 * REAL today: status, id, Org name, Last seen, Public IP, Backend, Location.
 * PLACEHOLDER (DASH / pending rings) until telemetry lands on NATS: CPU/MEM/TEMP, Uptime.
 */
import { html, nothing, type TemplateResult } from 'lit';
import { statusBadge } from '@vitesse/base-ui';
import { DASH, statusColor, statusLabel, orgLabel, relativeTime, formatUptime, freshOnly } from '../core/fleet-model';
import { ft } from '../core/i18n';
import type { FleetGateway } from '../core/fleet-client';
import { ring } from './ring';

const short = (s: string, a = 8, b = 6): string =>
  s.length > a + b + 1 ? `${s.slice(0, a)}…${s.slice(-b)}` : s;

function hw(icon: string, label: string, value: string, accent = false): TemplateResult {
  const placeholder = !value || value === DASH;
  return html`<div class="fgc-hw-item">
    <vitesse-icon name=${icon} size="12"></vitesse-icon>
    <span class="fgc-hw-label">${label}</span>
    <span class="fgc-hw-value ${placeholder ? 'is-placeholder' : ''} ${accent ? 'is-accent' : ''}"
      >${placeholder ? DASH : value}</span
    >
  </div>`;
}

export function fleetGatewayCard(
  g: FleetGateway,
  onOpen: (g: FleetGateway) => void,
  orgNames?: Map<string, string>,
): TemplateResult {
  const name = g.displayName || short(g.deviceFingerprint);
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpen(g);
    }
  };
  return html`
    <div
      class="base-overview-card clickable fleet-gw-card"
      role="button"
      tabindex="0"
      @click=${() => onOpen(g)}
      @keydown=${onKey}
    >
      <div class="fgc-head">
        <div class="fgc-head-main">
          <h3 class="base-overview-card-title">
            <span class="base-overview-card-title-icon"
              ><vitesse-icon name="hard-drive" size="16"></vitesse-icon
            ></span>
            <span class="base-overview-card-title-text" title=${name}>${name}</span>
          </h3>
          ${g.displayName
            ? html`<div class="fgc-id" title=${g.deviceFingerprint}>${short(g.deviceFingerprint, 8, 8)}</div>`
            : nothing}
        </div>
        <div class="fgc-head-badges">
          ${statusBadge(statusColor(g.status), statusLabel(g.status))}
          ${g.revoked ? statusBadge('red', ft('status.revoked')) : nothing}
        </div>
      </div>

      <div class="fgc-rings">
        ${ring({ value: freshOnly(g.healthTsMs, g.cpuPct || null), label: ft('ring.cpu'), size: 38, stroke: 3.5 })}
        ${ring({ value: freshOnly(g.healthTsMs, g.memPct || null), label: ft('ring.mem'), size: 38, stroke: 3.5 })}
        ${ring({ value: freshOnly(g.healthTsMs, g.tempC || null), label: ft('ring.temp'), unit: '°', max: 90, size: 38, stroke: 3.5 })}
      </div>

      <div class="fgc-hw">
        ${hw('building-2', ft('card.org'), orgLabel(g.orgId, orgNames))}
        ${hw('activity', ft('card.lastSeen'), relativeTime(g.lastSeenMs))}
        ${hw('globe', ft('card.publicIp'), g.publicIp || DASH, !!g.publicIp)}
        ${hw('package', ft('card.backend'), g.softwareVersion || DASH)}
        ${hw('map-pin', ft('card.location'), g.geo || DASH)}
        ${hw('activity', ft('card.uptime'), formatUptime(g.uptimeS))}
      </div>
    </div>
  `;
}
